import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'convex/react'
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  BookOpenIcon,
  StarIcon,
  CodeBracketSquareIcon,
  FireIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { api } from '../convex_generated/api'
import { BOOK_GENRES, GENRE_BADGE_COLORS, type BookGenre } from '../constants/genres'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type SortBy = 'newest' | 'rating' | 'trending'
type Genre = BookGenre

const ALL_GENRES: Genre[] = [...BOOK_GENRES]

const GENRE_COLORS: Record<Genre, string> = GENRE_BADGE_COLORS

interface StorefrontBook {
  _id: string
  title: string
  originalAuthor: string
  description: string
  coverImage: string
  genre: string[]
  isPublicDomain: boolean
  inProgress?: boolean
  branchCount: number
  averageRating: number
  totalRatings: number
  createdAt: number
  rootPublished?: boolean
  rootVisibility?: 'public' | 'private' | 'paid' | 'in-progress' | null
}

// ---------------------------------------------------------------------------
// StarRating
// ---------------------------------------------------------------------------
const StarRating: React.FC<{ rating: number; count?: number; size?: 'sm' | 'md' }> = ({
  rating,
  count,
  size = 'sm',
}) => {
  const cls = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((s) => (
          s <= Math.round(rating)
            ? <StarIconSolid key={s} className={`${cls} text-amber-400`} />
            : <StarIcon key={s} className={`${cls} text-gray-300 dark:text-gray-600`} />
        ))}
      </div>
      {count !== undefined && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {rating > 0 ? rating.toFixed(1) : '—'} {count > 0 && `(${count})`}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// BookCard
// ---------------------------------------------------------------------------
const StorefrontBookCard: React.FC<{ book: StorefrontBook }> = ({ book }) => {
  const [imgError, setImgError] = useState(false)

  return (
    <Link
      to={`/branches/${book._id}`}
      className="group flex flex-col bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:-translate-y-0.5"
    >
      {/* Cover */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600">
        {!imgError && book.coverImage ? (
          <img
            src={book.coverImage}
            alt={book.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
            <BookOpenIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-2" />
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium leading-tight">
              {book.title}
            </span>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {book.isPublicDomain && (
            <span className="px-1.5 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded">
              FREE
            </span>
          )}
          {book.rootVisibility === 'in-progress' && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded">
              <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
              IN PROGRESS
            </span>
          )}
        </div>

        {/* Genre pills */}
        <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1">
          {book.genre.slice(0, 2).map((g) => (
            <span
              key={g}
              className={`px-1.5 py-0.5 text-[10px] font-semibold rounded capitalize ${
                GENRE_COLORS[g as Genre] ?? 'bg-gray-100 text-gray-700'
              }`}
            >
              {g}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-3">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-snug line-clamp-2 mb-0.5 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
          {book.title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          by {book.originalAuthor}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 flex-1 mb-2">
          {book.description}
        </p>

        <div className="flex items-center justify-between mt-auto">
          <StarRating rating={book.averageRating} count={book.totalRatings} />
          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <CodeBracketSquareIcon className="w-3.5 h-3.5" />
            {book.branchCount > 0
              ? `${book.branchCount} ${book.branchCount === 1 ? 'branch' : 'branches'}`
              : 'Original'}
          </span>
        </div>
      </div>
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------
const BookCardSkeleton: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 animate-pulse">
    <div className="aspect-[3/4] bg-gray-200 dark:bg-gray-700" />
    <div className="p-3 space-y-2">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// StorefrontPage
// ---------------------------------------------------------------------------
const StorefrontPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedGenres, setSelectedGenres] = useState<Genre[]>([])
  const [selectedAuthor, setSelectedAuthor] = useState<string>('')
  const [sortBy, setSortBy] = useState<SortBy>('newest')
  const [showFilters, setShowFilters] = useState(false)

  // Debounce search by letting React just pass searchQuery directly —
  // Convex's client re-runs the query when args change, which is fast enough.
  const allBooks = useQuery(api.books.getStorefrontBooks, {
    genre: selectedGenres.length === 1 ? selectedGenres[0] : undefined,
    sortBy,
    searchQuery: searchQuery.trim() || undefined,
    limit: 48,
  })

  // Client-side multi-genre + author filter (Convex only supports single genre index)
  const books = useMemo<StorefrontBook[] | undefined>(() => {
    if (!allBooks) return undefined
    let filtered = allBooks as StorefrontBook[]
    if (selectedGenres.length > 1) {
      filtered = filtered.filter((b) =>
        selectedGenres.every((g) => b.genre.includes(g)),
      )
    }
    if (selectedAuthor) {
      filtered = filtered.filter((b) => b.originalAuthor === selectedAuthor)
    }
    return filtered
  }, [allBooks, selectedGenres, selectedAuthor])

  // Unique authors from the full book list (before author filter)
  const uniqueAuthors = useMemo(() => {
    if (!allBooks) return []
    const authors = [...new Set((allBooks as StorefrontBook[]).map((b) => b.originalAuthor))]
    return authors.sort((a, b) => a.localeCompare(b))
  }, [allBooks])

  const toggleGenre = (genre: Genre) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre],
    )
  }

  const clearFilters = () => {
    setSelectedGenres([])
    setSelectedAuthor('')
    setSortBy('newest')
  }

  const hasActiveFilters = selectedGenres.length > 0 || sortBy !== 'newest' || selectedAuthor !== ''

  // Split into featured (top 3) and grid
  const featuredBooks = books?.filter((b) => b.branchCount > 0).slice(0, 3) ?? []
  const isLoading = books === undefined

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Toolbar */}
      {/* Search + Toolbar */}
      <div className="sticky top-16 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 space-y-2">
          {/* Search bar */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search books, authors…"
              className="w-full pl-9 pr-4 py-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder-gray-400"
            />
          </div>
          {/* Genre pills + filters */}
          <div className="flex items-center justify-between gap-3">
          {/* Genre pills */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1 flex-1 min-w-0">
            {ALL_GENRES.map((g) => (
              <button
                key={g}
                onClick={() => toggleGenre(g)}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
                  selectedGenres.includes(g)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Right: Filter toggle + Sort */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
            <button
              onClick={() => setShowFilters((f) => !f)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showFilters
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <FunnelIcon className="w-4 h-4" />
              Filters
            </button>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-0 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="trending">Trending</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
          </div>
        </div>

        {/* Expanded filter panel */}
        {showFilters && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-4">
            <div className="max-w-7xl mx-auto space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Genres
                </p>
                <div className="flex flex-wrap gap-2">
                  {ALL_GENRES.map((g) => (
                    <button
                      key={g}
                      onClick={() => toggleGenre(g)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                        selectedGenres.includes(g)
                          ? 'bg-primary-600 text-white'
                          : `${GENRE_COLORS[g]} hover:opacity-80`
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Author
                </p>
                <select
                  value={selectedAuthor}
                  onChange={(e) => setSelectedAuthor(e.target.value)}
                  className="text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-0 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer min-w-[180px]"
                >
                  <option value="">All authors</option>
                  {uniqueAuthors.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Sort by
                </p>
                <div className="flex gap-2">
                  {([
                    { value: 'newest', label: 'Newest', icon: ClockIcon },
                    { value: 'trending', label: 'Trending', icon: FireIcon },
                    { value: 'rating', label: 'Top Rated', icon: StarIconSolid },
                  ] as const).map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setSortBy(value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        sortBy === value
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        {/* Featured strip (books with branches) */}
        {!isLoading && featuredBooks.length > 0 && !searchQuery && selectedGenres.length === 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <FireIcon className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Active Stories</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">— books with community branches</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredBooks.map((book) => (
                <Link
                  key={book._id}
                  to={`/branches/${book._id}`}
                  className="group flex gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all duration-200"
                >
                  <div className="w-16 flex-shrink-0 aspect-[3/4] rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700">
                    <img
                      src={book.coverImage}
                      alt={book.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const t = e.target as HTMLImageElement
                        t.src = ''
                        t.style.display = 'none'
                      }}
                    />
                  </div>
                  <div className="flex flex-col justify-center min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {book.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">by {book.originalAuthor}</p>
                    <StarRating rating={book.averageRating} count={book.totalRatings} />
                    <p className="text-xs text-primary-600 dark:text-primary-400 mt-1 font-medium">
                      {book.branchCount} {book.branchCount === 1 ? 'branch' : 'branches'} →
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Main grid */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {sortBy === 'trending' ? 'Trending' : sortBy === 'rating' ? 'Top Rated' : 'All Books'}
              </h2>
              {!isLoading && (
                <span className="text-sm text-gray-400 dark:text-gray-500">
                  {books!.length} {books!.length === 1 ? 'book' : 'books'}
                </span>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <BookCardSkeleton key={i} />
              ))}
            </div>
          ) : books!.length === 0 ? (
            <div className="text-center py-20">
              <BookOpenIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {searchQuery ? 'No books match your search' : 'No books yet'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {searchQuery
                  ? 'Try different keywords or clear your filters.'
                  : 'The library is empty. Admins can seed it with public domain books.'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {books!.map((book) => (
                <StorefrontBookCard key={book._id} book={book} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export default StorefrontPage
